/* ============================================================
   selectors.js — pure derivations over the loaded state.
   No I/O here: every function takes the state it needs.
   ============================================================ */

export function isoWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - firstDayNum + 3);
  return 1 + Math.round((d - firstThu) / (7 * 24 * 3600 * 1000));
}

export function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Mon = 0
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day);
  return d;
}

export function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

export function kr(n) {
  return Math.round(n).toLocaleString('sv-SE') + ' kr';
}

export function timeAgo(ts) {
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return 'nyss';
  if (m < 60) return m + ' min sedan';
  const h = Math.round(m / 60);
  if (h < 24) return h + ' h sedan';
  return Math.round(h / 24) + ' dgr sedan';
}

/* ---------- lager ---------- */

/** Utan lagersaldo: hela grupper (Hembakat) eller enskilda varor (kaffe). */
export function isUntracked(state, p) {
  if (p.untracked) return true;
  const g = (state.groups || []).find(x => x.id === p.groupId);
  return !!(g && g.untracked);
}

/** Gruppens rabatt i procent för varan, 0 = ingen. */
export function discountFor(state, p) {
  const g = state && (state.groups || []).find(x => x.id === p.groupId);
  const d = g ? Math.round(+g.discount || 0) : 0;
  return Math.min(100, Math.max(0, d));
}

/**
 * Priset kassan tar. En grupprabatt (t.ex. 50 % på all glass) räknas på
 * ordinarie pris och trumfar varans extrapris; utan rabatt gäller
 * extrapriset när ett är satt, annars ordinarie. Speglar repo.priceOf.
 */
export function priceFor(p, state) {
  const d = discountFor(state, p);
  if (d > 0) return Math.round((p.price || 0) * (1 - d / 100));
  return p.salePrice != null ? p.salePrice : p.price;
}

/** Inköpspris: receptvaror härleds från sina lagervaror. */
export function costFor(state, p) {
  if (!p.recipe || !p.recipe.length) return p.cost || 0;
  return p.recipe.reduce((a, [iid, q]) => {
    const ing = (state.ingredients || []).find(i => i.id === iid);
    return a + (ing ? (ing.cost || 0) * q : 0);
  }, 0);
}

/** Härlett lager: receptvaror begränsas av sina lagervaror. */
export function stockFor(state, p) {
  if (!p) return 0;
  if (isUntracked(state, p)) return Infinity;
  if (!p.recipe || !p.recipe.length) return p.stock;
  return Math.min(...p.recipe.map(([iid, q]) => {
    const ing = (state.ingredients || []).find(i => i.id === iid);
    return ing ? Math.floor(ing.stock / q) : 0;
  }));
}

/**
 * Everything that can be restocked directly, low-to-empty first.
 *
 * Covers varor som säljs i kassan *and* lagervaror — servetter, muggar and
 * ketchup are bought and counted like anything else, they just never get rung
 * up. Recipe products are left out: their stock is derived, so the lagervara
 * behind them is what actually needs filling.
 */
export function restockList(state) {
  const rows = [
    ...state.products.filter(p => !p.recipe && !isUntracked(state, p)).map(p => ({ ...p, kind: 'prod' })),
    ...(state.ingredients || []).map(i => ({ ...i, kind: 'ing' })),
  ];
  return rows
    .filter(r => r.stock <= state.settings.lowStock)
    .sort((a, b) => a.stock - b.stock);
}

/** Lagervärde till inköpspris — receptvaror räknas via sina lagervaror. */
export function stockValue(state) {
  return state.products.filter(p => !p.recipe && !isUntracked(state, p)).reduce((a, p) => a + p.cost * p.stock, 0)
    + (state.ingredients || []).reduce((a, i) => a + i.cost * i.stock, 0);
}

export function retailValue(state) {
  return state.products.filter(p => !isUntracked(state, p)).reduce((a, p) => a + p.price * stockFor(state, p), 0);
}

/* ---------- schema ---------- */

export function teamForWeek(state, date = new Date()) {
  const w = isoWeek(date);
  const y = date.getFullYear();
  const hit = (state.schedule || []).find(x => x.week === w && x.year === y);
  return hit ? hit.team : null;
}

/** ISO-veckodag: 1 = måndag … 7 = söndag. */
export function isoDay(date = new Date()) {
  return ((date.getDay() + 6) % 7) + 1;
}

export const DAY_NAMES = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];   // index = isoDay - 1

/**
 * Grillas det just nu? Styr om kiosken visar Grillen-fliken och grillräknaren
 * på Hamburgarmålet. Veckan måste vara ibockad som grillvecka, och har admin
 * valt dagar måste dagens veckodag vara en av dem — utan valda dagar gäller
 * hela veckan.
 */
export function grillActive(state, date = new Date()) {
  const hit = (state.schedule || []).find(x => x.week === isoWeek(date) && x.year === date.getFullYear());
  if (!hit || !hit.grill) return false;
  const days = hit.grillDays || [];
  return days.length === 0 || days.includes(isoDay(date));
}

/* ---------- domare & ledare ---------- */

/** Betalsätten som betyder "lämnades ut gratis" — raderna är bokförda till 0 kr. */
export const FREE_PAYMENTS = { domare: 'Domare', ledare: 'Ledare' };

export function isFreeSale(sale) {
  return !!(sale && FREE_PAYMENTS[sale.payment]);
}

/**
 * Varorna admin bockat i som gratis till ledare (kaffe under match) och till
 * domare (korv med bröd). Samma vara kan stå under båda.
 */
export function freebies(state) {
  const products = state.products || [];
  return {
    ledare: products.filter(p => p.freeForLeaders),
    domare: products.filter(p => p.freeForReferees),
  };
}

/* ---------- försäljning ---------- */

export function revenueBetween(state, from, to) {
  let rev = 0, profit = 0, count = 0;
  for (const s of state.sales) {
    if (s.ts >= from && s.ts < to) {
      rev += s.total;
      profit += s.profit;
      count++;
    }
  }
  return { rev, profit, count };
}

/** Flytta en referensdag `n` perioder (negativt = bakåt). */
export function shiftPeriod(period, ref, n) {
  const d = new Date(ref);
  if (period === 'day') d.setDate(d.getDate() + n);
  else if (period === 'week') d.setDate(d.getDate() + 7 * n);
  else if (period === 'month') d.setDate(1), d.setMonth(d.getMonth() + n);
  else d.setMonth(0, 1), d.setFullYear(d.getFullYear() + n);
  return d;
}

const MONTH_LONG = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];

/** "Måndag 21 september", "Vecka 39 · 2026", "September 2026", "2026". */
export function periodLabel(period, ref = new Date()) {
  const d = new Date(ref);
  if (period === 'day') {
    const s = d.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  if (period === 'week') return `Vecka ${isoWeek(d)} · ${d.getFullYear()}`;
  if (period === 'month') return `${MONTH_LONG[d.getMonth()].charAt(0).toUpperCase()}${MONTH_LONG[d.getMonth()].slice(1)} ${d.getFullYear()}`;
  return String(d.getFullYear());
}

export function rangeFor(period, ref = new Date()) {
  const d = new Date(ref);
  if (period === 'day') {
    const s = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return [s.getTime(), addDays(s, 1).getTime()];
  }
  if (period === 'week') {
    const s = startOfWeek(d);
    return [s.getTime(), addDays(s, 7).getTime()];
  }
  if (period === 'month') {
    return [new Date(d.getFullYear(), d.getMonth(), 1).getTime(), new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime()];
  }
  return [new Date(d.getFullYear(), 0, 1).getTime(), new Date(d.getFullYear() + 1, 0, 1).getTime()];
}

/**
 * Delar upp en redan filtrerad och sorterad köplista i kalenderdagar, nyast
 * först, med dagens summor färdigräknade. Kioskveckan är några matchdagar och
 * inte sju jämna — därför finns bara de dagar som faktiskt har köp.
 */
export function salesByDay(sales) {
  const days = new Map();
  for (const sale of sales) {
    const d = new Date(sale.ts);
    const ts = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    let day = days.get(ts);
    if (!day) days.set(ts, (day = { ts, rows: [], total: 0, profit: 0 }));
    day.rows.push(sale);
    day.total += sale.total;
    day.profit += sale.profit;
  }
  return [...days.values()].sort((a, b) => b.ts - a.ts);
}

export function productTallies(state, from, to) {
  const map = {};
  for (const s of state.sales) {
    if (s.ts < from || s.ts >= to) continue;
    for (const it of s.items) {
      const m = map[it.productId] || (map[it.productId] = { qty: 0, rev: 0, name: it.name, emoji: it.emoji });
      m.qty += it.qty;
      m.rev += it.price * it.qty;
    }
  }
  return Object.values(map).sort((a, b) => b.qty - a.qty);
}

export function weeklySeries(state, weeks = 10) {
  const out = [];
  const now = new Date();
  for (let i = weeks - 1; i >= 0; i--) {
    const ws = startOfWeek(addDays(now, -i * 7));
    const we = addDays(ws, 7);
    const { rev, profit } = revenueBetween(state, ws.getTime(), we.getTime());
    out.push({ week: isoWeek(ws), rev, profit, from: ws.getTime(), to: we.getTime() });
  }
  return out;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export function monthlySeries(state, months = 12) {
  const out = [];
  const now = new Date();
  for (let i = months - 1; i >= 0; i--) {
    const s = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const e = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    const { rev, profit } = revenueBetween(state, s.getTime(), e.getTime());
    out.push({ m: MONTHS[s.getMonth()], rev, profit, from: s.getTime(), to: e.getTime() });
  }
  return out;
}

export function yearlySeries(state, years = 3) {
  const out = [];
  const y = new Date().getFullYear();
  for (let i = years - 1; i >= 0; i--) {
    const s = new Date(y - i, 0, 1);
    const e = new Date(y - i + 1, 0, 1);
    const { rev, profit } = revenueBetween(state, s.getTime(), e.getTime());
    out.push({ y: String(y - i), rev, profit, from: s.getTime(), to: e.getTime() });
  }
  return out;
}

/* ---------- svinn ---------- */

export function wasteBetween(state, from, to) {
  const rows = (state.waste || []).filter(w => w.ts >= from && w.ts < to);
  return {
    qty: rows.reduce((a, w) => a + w.qty, 0),
    value: rows.reduce((a, w) => a + w.value, 0),
    rows,
  };
}
